import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { IPersonalizedPracticeSession } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session';
import { TranslationService } from 'src/app/services/translation.service';
import { LocaleService } from 'src/app/services/locale.service';
import { formatLabel } from 'src/app/utils/japanese-punctuation';

// Register Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-personalized-practice-chart',
  imports: [CommonModule, TranslocoModule],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  templateUrl: './personalized-practice-chart.component.html',
  styleUrl: './personalized-practice-chart.component.scss',
})
export class PersonalizedPracticeChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('chartCanvas', { static: false })
  chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() sessionData: IPersonalizedPracticeSession[] = [];
  private chart: Chart | null = null;
  private tooltipTimeout: any = null;
  private isTooltipHovered: boolean = false;

  constructor(
    private translationService: TranslationService,
    private localeService: LocaleService
  ) {}

  ngAfterViewInit(): void {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['sessionData'] &&
      !changes['sessionData'].firstChange &&
      this.chart
    ) {
      this.updateChart();
    }
  }

  private initializeChart(): void {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');

    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    const chartData = this.prepareChartData();
    const xAxisConfig = this.calculateXAxisRange(chartData.length);

    const config: ChartConfiguration<'scatter'> = {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: this.translationService.translate(
              'OVERALL_SCORE_LABEL',
              'personalizedPractice'
            ),
            data: chartData,
            backgroundColor: '#52A4B0',
            borderColor: '#52A4B0',
            borderWidth: 1,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: (context) => this.externalTooltipHandler(context),
          },
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            min: xAxisConfig.min,
            max: xAxisConfig.max,
            title: {
              display: true,
              text: this.translationService.translate(
                'TIME',
                'personalizedPractice'
              ),
            },
            ticks: {
              display: false,
              stepSize: 0,
            },
            grid: {
              display: false,
            },
          },
          y: {
            min: 0,
            max: 5,
            ticks: {
              stepSize: 1,
            },
            title: {
              display: true,
              text: this.translationService.translate(
                'SCORE',
                'personalizedPractice'
              ),
            },
          },
        },
      },
    };

    this.chart = new Chart(ctx, config);
  }

  private prepareChartData(): {
    x: number;
    y: number;
    sessionDate: string;
    overallScore: number;
  }[] {
    if (!this.sessionData || this.sessionData.length === 0) {
      return [];
    }

    // Sort sessions by date to show chronological progression
    const sortedSessions = [...this.sessionData].sort((a, b) => {
      return new Date(a.scoredAt).getTime() - new Date(b.scoredAt).getTime();
    });

    // Map each session to a point on the chart, filtering out sessions with null scores
    return sortedSessions
      .filter((session) => session.scoreOverall !== null)
      .map((session, index) => ({
        x: index,
        y: session.scoreOverall!,
        sessionDate: session.scoredAt,
        overallScore: session.scoreOverall!,
      }));
  }

  private calculateXAxisRange(dataLength: number): { min: number; max: number } {
    // For 2 or fewer points, use a fixed range to keep dots closer together
    if (dataLength <= 2) {
      return { min: -1.5, max: 2.5 };
    }

    // For 3-4 points, add some padding
    if (dataLength <= 4) {
      return { min: -0.5, max: dataLength - 0.5 };
    }

    // For 5+ points, use minimal padding
    return { min: -0.2, max: dataLength - 0.8 };
  }

  private updateChart(): void {
    if (!this.chart) return;

    const chartData = this.prepareChartData();
    const xAxisConfig = this.calculateXAxisRange(chartData.length);

    this.chart.data.datasets[0].data = chartData;
    this.chart.options.scales!['x']!.min = xAxisConfig.min;
    this.chart.options.scales!['x']!.max = xAxisConfig.max;
    this.chart.update();
  }

  private externalTooltipHandler(context: any): void {
    const { chart, tooltip } = context;

    // Get or create tooltip element
    let tooltipEl = document.getElementById('chartjs-tooltip');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      tooltipEl.style.background = 'rgba(0, 0, 0, 0.8)';
      tooltipEl.style.borderRadius = '3px';
      tooltipEl.style.color = 'white';
      tooltipEl.style.opacity = '1';
      tooltipEl.style.pointerEvents = 'auto';
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.transform = 'translate(-50%, 0)';
      tooltipEl.style.transition = 'all .1s ease';
      tooltipEl.style.padding = '8px 12px';
      tooltipEl.style.fontSize = '12px';
      tooltipEl.style.userSelect = 'text';
      tooltipEl.style.cursor = 'text';
      tooltipEl.style.zIndex = '1000';

      // Keep tooltip visible when hovering over it
      tooltipEl.addEventListener('mouseenter', () => {
        this.isTooltipHovered = true;
        if (this.tooltipTimeout) {
          clearTimeout(this.tooltipTimeout);
          this.tooltipTimeout = null;
        }
      });

      tooltipEl.addEventListener('mouseleave', () => {
        this.isTooltipHovered = false;
        this.hideTooltipWithDelay();
      });

      document.body.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      this.hideTooltipWithDelay();
      return;
    }

    // Clear any existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }

    // Set content
    if (tooltip.body) {
      const dataPoint = tooltip.dataPoints[0].raw as {
        x: number;
        y: number;
        sessionDate: string;
        overallScore: number;
      };

      const sessionDateLabel = this.translationService.translate(
        'SESSION_DATE',
        'personalizedPractice'
      );
      const sessionScoreLabel = this.translationService.translate(
        'SESSION_SCORE',
        'personalizedPractice'
      );
      const formattedDate = this.localeService.formatDate(dataPoint.sessionDate);

      const container = document.createElement('div');
      container.style.lineHeight = '1.5';

      const dateDiv = document.createElement('div');
      const lang = this.localeService.getCurrentLanguage();
      dateDiv.textContent = formatLabel(sessionDateLabel, lang, formattedDate);

      const scoreDiv = document.createElement('div');
      scoreDiv.textContent = formatLabel(sessionScoreLabel, lang, dataPoint.overallScore);

      container.appendChild(dateDiv);
      container.appendChild(scoreDiv);

      tooltipEl.replaceChildren(container);
    }

    // Position tooltip to the left of the point
    const position = chart.canvas.getBoundingClientRect();
    tooltipEl.style.opacity = '1';
    tooltipEl.style.left =
      position.left + window.pageXOffset + tooltip.caretX + 'px';
    tooltipEl.style.top =
      position.top + window.pageYOffset + tooltip.caretY + 'px';
  }

  private hideTooltipWithDelay(): void {
    this.tooltipTimeout = setTimeout(() => {
      // Only hide if user is not hovering over the tooltip
      if (!this.isTooltipHovered) {
        const tooltipEl = document.getElementById('chartjs-tooltip');
        if (tooltipEl) {
          tooltipEl.style.opacity = '0';
        }
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }

    // Clean up tooltip
    const tooltipEl = document.getElementById('chartjs-tooltip');
    if (tooltipEl) {
      tooltipEl.remove();
    }

    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
  }
}
